// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DateTime } from 'luxon';
import { z } from 'zod';

function addInvalidDateIssue(ctx: z.RefinementCtx) {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: 'Invalid date',
  });

  return z.NEVER;
}

function isValidDateTime(value: unknown): value is DateTime {
  return DateTime.isDateTime(value) && value.isValid;
}

function parseDateTime(createDateTime: () => DateTime): DateTime | null {
  try {
    const dateTime = createDateTime();
    return isValidDateTime(dateTime) ? dateTime : null;
  } catch {
    return null;
  }
}

function parseISODateTime(value: string, options?: { setZone?: boolean }) {
  return parseDateTime(() => DateTime.fromISO(value, options));
}

function parseDateOnly(value: string) {
  return parseDateTime(() =>
    DateTime.fromFormat(value, 'yyyy-MM-dd', {
      zone: 'local',
    }).startOf('day')
  );
}

export const dateTimeSchema = z.union([
  z.custom<DateTime>((value) => isValidDateTime(value), {
    message: 'Date is required',
  }),
  z.string().transform((value, ctx) => {
    const dateTime = parseISODateTime(value);
    return dateTime ?? addInvalidDateIssue(ctx);
  }),
]);

/**
 * Schema for parsing date strings (YYYY-MM-DD) into DateTime objects.
 * Converts the date to midnight local time to ensure consistent handling in user's browser.
 * This is used for fields that store only dates without time components.
 */

export const dateSchema = z.union([
  z.custom<DateTime>((value) => isValidDateTime(value), {
    message: 'Date is required',
  }),
  z.string().transform((value, ctx) => {
    const dateOnly = parseDateOnly(value);
    if (dateOnly !== null) {
      return dateOnly;
    }

    const isoDateTime = parseISODateTime(value, { setZone: true });
    return isoDateTime?.startOf('day') ?? addInvalidDateIssue(ctx);
  }),
]);

// Accepts a luxon DateTime or null (cleared state).
export const dateTimeOrNullSchema = z.custom<DateTime | null>(
  (value) => value === null || DateTime.isDateTime(value),
  { message: 'Invalid date' }
);

/**
 * Schema for optional date-only route search parameters. Normalizes valid
 * values to YYYY-MM-DD strings and drops invalid or missing values.
 */
export const routeDateSchema = dateSchema
  .transform((date) => date.toISODate())
  .optional()
  .catch(undefined);
